import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";

import PendingOrdersService from "../../services/pendingOrdersService";

const formatDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB");
};

const formatMoney = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(
    v || 0
  );

const DataRow = ({ label, value, copyable }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "180px 1fr" },
      gap: { xs: 0.25, sm: 2 },
      py: 1,
      borderBottom: (t) => `1px dashed ${t.palette.divider}`,
      "&:last-of-type": { borderBottom: 0 },
    }}
  >
    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, alignSelf: "center" }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: "text.primary",
        fontWeight: 500,
        fontFamily: copyable ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
        fontSize: copyable ? 12 : 14,
        wordBreak: "break-all",
      }}
    >
      {value || <Box component="span" sx={{ color: "text.disabled" }}>—</Box>}
    </Typography>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography
    variant="overline"
    sx={{
      display: "block",
      fontWeight: 700,
      color: "text.secondary",
      letterSpacing: 0.8,
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

export const PendingOrderPage = (props) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const service = PendingOrdersService();

  const [lines, setLines] = useState(null);
  const [linesLoading, setLinesLoading] = useState(true);

  const item = props.item || {};

  useEffect(() => {
    if (!item.no) {
      setLinesLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const fetched = await service.getPendingOrderLines(item.no);
        if (!cancelled) setLines(Array.isArray(fetched) ? fetched : []);
      } catch (_) {
        if (!cancelled) setLines([]);
      } finally {
        if (!cancelled) setLinesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item.no]);

  const rows = (lines || []).map((l, i) => ({
    key: `${l.lineNumber || l.lineNo || i}-${i}`,
    lineNo: l.lineNumber || l.lineNo,
    objectNumber: l.lineObjectNumber || l.no,
    description: l.description,
    unitOfMeasureCode: l.unitOfMeasureCode,
    quantity: l.quantity,
    unitPrice: l.unitPrice,
    lineAmount: l.lineAmountExclVAT,
  }));

  return (
    <Box>
      {/* Action bar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Button
          variant="text"
          color="inherit"
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={props.onBack}
          sx={{ color: "text.secondary", alignSelf: "flex-start" }}
        >
          Back to pending orders
        </Button>
      </Stack>

      {/* Header strip */}
      <Card
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}>
            Pending order
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.25, lineHeight: 1.1 }}>
            {item.no || "—"}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={formatDate(item.postingDate)} sx={{ bgcolor: "background.default" }} />
            {item.serviceType && (
              <Chip
                size="small"
                label={item.serviceType.trim()}
                sx={{
                  bgcolor: "rgba(245, 166, 35, 0.18)",
                  color: "text.primary",
                  fontWeight: 600,
                }}
              />
            )}
            {item.status && (
              <Chip
                size="small"
                label={item.status}
                sx={{
                  bgcolor: "rgba(27, 122, 62, 0.14)",
                  color: "#1b7a3e",
                  fontWeight: 700,
                }}
              />
            )}
          </Stack>
        </Box>
      </Card>

      {/* Two-column body */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 320px" },
          gap: 3,
          alignItems: "start",
          mb: 3,
        }}
      >
        <Card
          sx={{
            p: { xs: 2, md: 3 },
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: "none",
          }}
        >
          <SectionTitle>Customer</SectionTitle>
          <Box sx={{ mb: 3 }}>
            <DataRow
              label="Sell-to"
              value={
                item.selltoCustomerName
                  ? `${item.selltoCustomerNo || ""} · ${item.selltoCustomerName}`
                  : null
              }
            />
          </Box>

          <SectionTitle>Fitter & Location</SectionTitle>
          <Box sx={{ mb: 3 }}>
            <DataRow
              label="Fitter"
              value={item.fitterName ? `${item.fitterNo || ""} · ${item.fitterName}` : null}
            />
            <DataRow label="Location" value={item.location} />
            <DataRow label="Warehouse" value={item.locationCode} />
            <DataRow label="Salesperson" value={item.salesPersonCode} />
          </Box>

          <SectionTitle>Vehicle</SectionTitle>
          <Box>
            <DataRow label="Vehicle reg" value={item.vehicleRegistrationNumber} />
            <DataRow label="Mileage" value={item.mileage ? String(item.mileage) : null} />
            <DataRow label="Docket no" value={item.docketNo} />
            <DataRow label="ReTorque" value={item.reTorque ? "Yes" : "No"} />
            <DataRow label="Tyre check" value={item.tyreCheck ? "Yes" : "No"} />
            <DataRow label="System id" value={item.documentNumber} copyable />
          </Box>
        </Card>

        <Card
          sx={{
            p: { xs: 2, md: 3 },
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: "none",
            position: { md: "sticky" },
            top: { md: 80 },
          }}
        >
          <SectionTitle>Order</SectionTitle>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Number
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.no || "—"}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Posting date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatDate(item.postingDate)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Service
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                {item.serviceType ? item.serviceType.trim() : "—"}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.status || "—"}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Job status
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.jobStatus || "—"}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Lines
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {linesLoading ? "…" : rows.length}
              </Typography>
            </Stack>
          </Stack>
        </Card>
      </Box>

      {/* Lines */}
      <Card
        sx={{
          overflow: "hidden",
          border: (t) => `1px solid ${t.palette.divider}`,
          boxShadow: "none",
        }}
      >
        <Box sx={{ p: { xs: 2, md: 3 }, pb: 1.5 }}>
          <SectionTitle>Lines ({linesLoading ? "…" : rows.length})</SectionTitle>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "background.default" }}>
                {!isCompact && (
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                    Line
                  </TableCell>
                )}
                {!isCompact && (
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                    Object
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                  Description
                </TableCell>
                {!isCompact && (
                  <TableCell sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                    UoM
                  </TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                  Qty
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                  Unit price
                </TableCell>
                {!isCompact && (
                  <TableCell align="right" sx={{ fontWeight: 700, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>
                    Line total
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {linesLoading && (
                <TableRow>
                  <TableCell
                    colSpan={isCompact ? 3 : 7}
                    align="center"
                    sx={{ py: 4, color: "text.secondary" }}
                  >
                    <CircularProgress size={18} sx={{ mr: 1, verticalAlign: "middle" }} />
                    Loading lines…
                  </TableCell>
                </TableRow>
              )}
              {!linesLoading && rows.map((r) => (
                <TableRow key={r.key} hover>
                  {!isCompact && <TableCell>{r.lineNo}</TableCell>}
                  {!isCompact && (
                    <TableCell sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}>
                      {r.objectNumber}
                    </TableCell>
                  )}
                  <TableCell sx={{ fontSize: 13 }}>{r.description}</TableCell>
                  {!isCompact && <TableCell>{r.unitOfMeasureCode}</TableCell>}
                  <TableCell align="right">{r.quantity}</TableCell>
                  <TableCell align="right">{formatMoney(r.unitPrice)}</TableCell>
                  {!isCompact && (
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatMoney(r.lineAmount)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!linesLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={isCompact ? 3 : 7}
                    align="center"
                    sx={{ py: 4, color: "text.secondary" }}
                  >
                    No order lines
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};
